import { hubCall } from "@/lib/api/hub";
/**
 * Workflow Service — calls hub proxy for workflow-related endpoints
 */
import type {
  Workflow,
  WorkflowRun,
  WorkflowRunStep,
  WorkflowStep,
  WorkflowStats,
  WorkflowMCPTool,
  WorkflowNodeCatalog,
  WorkflowNodeType,
  WorkflowWebhookTrigger,
  Checkpoint,
  CheckpointFilters,
  CreateWorkflowWebhookPayload,
  CreateWorkflowPayload,
  UpdateWorkflowWebhookPayload,
  UpdateWorkflowPayload,
  UpdateWorkflowDefinitionPayload,
  WorkflowFilters,
  RunFilters,
} from "@/types/workflow.types";
import type {
  WorkflowReferenceOptionsParams,
  WorkflowReferenceOptionsResponse,
  WorkflowReferenceResolveRequest,
  WorkflowReferenceResolveResponse,
} from "@/types/workflow-reference.types";
import type { ApiResponse, PaginatedResponse, EnrichedPaginatedResponse } from "@/types/api.types";

function buildQueryString(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") {
      searchParams.set(key, String(value));
    }
  }
  const qs = searchParams.toString();
  return qs ? `?${qs}` : "";
}

export const workflowService = {
  // =========================================================================
  // Workflow CRUD
  // =========================================================================

  async list(filters?: WorkflowFilters): Promise<EnrichedPaginatedResponse<Workflow>> {
    const qs = filters ? buildQueryString(filters as Record<string, unknown>) : "";
    return hubCall<EnrichedPaginatedResponse<Workflow>>(`/workflows${qs}`);
  },

  async getById(id: string): Promise<ApiResponse<Workflow>> {
    return hubCall<ApiResponse<Workflow>>(`/workflows/${id}`);
  },

  async create(payload: CreateWorkflowPayload): Promise<ApiResponse<{ id: string }>> {
    return hubCall<ApiResponse<{ id: string }>>("/workflows", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  async update(id: string, payload: UpdateWorkflowPayload): Promise<ApiResponse<{ id: string }>> {
    return hubCall<ApiResponse<{ id: string }>>(`/workflows/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  },

  async delete(id: string): Promise<ApiResponse<{ id: string }>> {
    return hubCall<ApiResponse<{ id: string }>>(`/workflows/${id}`, {
      method: "DELETE",
    });
  },

  /** PATCH step uiPosition values — does not touch executable step config. */
  async updateDefinition(
    id: string,
    payload: UpdateWorkflowDefinitionPayload,
  ): Promise<ApiResponse<{ id: string }>> {
    return hubCall<ApiResponse<{ id: string }>>(`/workflows/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  },

  // =========================================================================
  // Workflow Runs
  // =========================================================================

  async run(id: string): Promise<ApiResponse<{ id: string }>> {
    return hubCall<ApiResponse<{ id: string }>>(`/workflows/${id}/run`, {
      method: "POST",
    });
  },

  async listRuns(
    workflowId: string,
    filters?: RunFilters,
  ): Promise<PaginatedResponse<WorkflowRun>> {
    const qs = filters ? buildQueryString(filters as Record<string, unknown>) : "";
    return hubCall<PaginatedResponse<WorkflowRun>>(`/workflows/${workflowId}/runs${qs}`);
  },

  async getRun(runId: string): Promise<ApiResponse<WorkflowRun>> {
    return hubCall<ApiResponse<WorkflowRun>>(`/workflow-runs/${runId}`);
  },

  async cancelRun(runId: string): Promise<ApiResponse<{ id: string }>> {
    return hubCall<ApiResponse<{ id: string }>>(`/workflow-runs/${runId}/cancel`, {
      method: "POST",
    });
  },

  /**
   * Resume a manually-paused workflow run (NOT for checkpoint pauses).
   * Backend rejects checkpoint-paused runs with 400 — those resume via
   * the approval flow. UI gates the button so this fails closed.
   */
  async resumeRun(runId: string): Promise<ApiResponse<{ id: string }>> {
    return hubCall<ApiResponse<{ id: string }>>(`/workflow-runs/${runId}/resume`, {
      method: "POST",
    });
  },

  /**
   * Retry a failed or cancelled run (clone-as-new-run). Returns the NEW
   * run id, not the source. Caller should optionally pass an
   * Idempotency-Key UUID so a rapid double-click does not create two
   * runs (BE dedupe TTL: 5min).
   */
  async retryRun(runId: string, idempotencyKey?: string): Promise<ApiResponse<{ id: string }>> {
    const headers: Record<string, string> = {};
    if (idempotencyKey) headers["Idempotency-Key"] = idempotencyKey;
    return hubCall<ApiResponse<{ id: string }>>(`/workflow-runs/${runId}/retry`, {
      method: "POST",
      headers,
    });
  },

  async getRunSteps(runId: string): Promise<ApiResponse<WorkflowRunStep[]>> {
    return hubCall<ApiResponse<WorkflowRunStep[]>>(`/workflow-runs/${runId}/steps`);
  },

  // =========================================================================
  // Workflow Steps
  // =========================================================================

  async listSteps(workflowId: string): Promise<ApiResponse<WorkflowStep[]>> {
    return hubCall<ApiResponse<WorkflowStep[]>>(`/workflows/${workflowId}/steps`);
  },

  async listMCPTools(): Promise<ApiResponse<WorkflowMCPTool[]>> {
    return hubCall<ApiResponse<WorkflowMCPTool[]>>("/workflows/mcp-tools");
  },

  async listNodeTypes(): Promise<ApiResponse<WorkflowNodeCatalog>> {
    return hubCall<ApiResponse<WorkflowNodeCatalog>>("/workflows/node-types");
  },

  async getNodeType(type: string): Promise<ApiResponse<WorkflowNodeType>> {
    return hubCall<ApiResponse<WorkflowNodeType>>(`/workflows/node-types/${type}`);
  },

  async listReferenceOptions(
    params: WorkflowReferenceOptionsParams,
  ): Promise<ApiResponse<WorkflowReferenceOptionsResponse>> {
    const qs = buildQueryString({ ...params });
    return hubCall<ApiResponse<WorkflowReferenceOptionsResponse>>(
      `/workflows/reference-options${qs}`,
    );
  },

  async resolveReferences(
    payload: WorkflowReferenceResolveRequest,
  ): Promise<ApiResponse<WorkflowReferenceResolveResponse>> {
    return hubCall<ApiResponse<WorkflowReferenceResolveResponse>>("/workflows/reference-resolve", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  /** Update a single step's executable config and optional connector binding. */
  async updateStepConfig(
    workflowId: string,
    stepId: string,
    config: Record<string, unknown>,
    connectorId?: string,
  ): Promise<ApiResponse<{ id: string }>> {
    return hubCall<ApiResponse<{ id: string }>>(`/workflows/${workflowId}/steps/${stepId}`, {
      method: "PUT",
      body: JSON.stringify({
        config,
        ...(connectorId ? { connectorId } : {}),
      }),
    });
  },

  /** Paginated event log for a run — used for REST replay on initial load
   *  and as fallback polling when SSE is unavailable. */
  async getRunEvents(
    runId: string,
    filters?: { sinceId?: string; limit?: number },
  ): Promise<
    import("@/types/api.types").PaginatedResponse<import("@/types/workflow.types").RunEvent>
  > {
    const qs = filters ? buildQueryString(filters as Record<string, unknown>) : "";
    return hubCall<
      import("@/types/api.types").PaginatedResponse<import("@/types/workflow.types").RunEvent>
    >(`/workflow-runs/${runId}/events${qs}`);
  },

  /** Move a workflow into a folder (folderId=null moves to root). */
  async moveToFolder(
    workflowId: string,
    folderId: string | null,
  ): Promise<ApiResponse<{ id: string }>> {
    return hubCall<ApiResponse<{ id: string }>>(`/workflows/${workflowId}/folder`, {
      method: "PATCH",
      body: JSON.stringify({ folderId }),
    });
  },

  /** Get active checkpoint for a run (if any). */
  async getRunCheckpoints(runId: string): Promise<ApiResponse<Checkpoint[]>> {
    return hubCall<ApiResponse<Checkpoint[]>>(`/workflow-runs/${runId}/checkpoints`);
  },

  // =========================================================================
  // Checkpoints (A4 endpoints)
  // =========================================================================

  /** GET /v1/cms/checkpoints — inbox for the current user, paginated. */
  async listCheckpoints(filters?: CheckpointFilters): Promise<PaginatedResponse<Checkpoint>> {
    const qs = filters ? buildQueryString(filters as Record<string, unknown>) : "";
    return hubCall<PaginatedResponse<Checkpoint>>(`/checkpoints${qs}`);
  },

  /** GET /v1/cms/checkpoints/:id — full detail with approvers list + form data. */
  async getCheckpoint(id: string): Promise<ApiResponse<Checkpoint>> {
    return hubCall<ApiResponse<Checkpoint>>(`/checkpoints/${id}`);
  },

  /** POST /v1/cms/checkpoints/:id/approve */
  async approveCheckpoint(
    id: string,
    payload: { formData?: Record<string, unknown> | null; comment?: string },
  ): Promise<ApiResponse<Checkpoint>> {
    return hubCall<ApiResponse<Checkpoint>>(`/checkpoints/${id}/approve`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  /** POST /v1/cms/checkpoints/:id/reject */
  async rejectCheckpoint(
    id: string,
    payload: { comment?: string },
  ): Promise<ApiResponse<Checkpoint>> {
    return hubCall<ApiResponse<Checkpoint>>(`/checkpoints/${id}/reject`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  /** GET /v1/cms/workflows/:id/stats — aggregate run metrics for one workflow. */
  async getWorkflowStats(id: string): Promise<ApiResponse<WorkflowStats>> {
    return hubCall<ApiResponse<WorkflowStats>>(`/workflows/${id}/stats`);
  },

  async listWebhooks(workflowId: string): Promise<ApiResponse<WorkflowWebhookTrigger[]>> {
    return hubCall<ApiResponse<WorkflowWebhookTrigger[]>>(`/workflows/${workflowId}/webhooks`);
  },

  async createWebhook(
    workflowId: string,
    payload: CreateWorkflowWebhookPayload,
  ): Promise<ApiResponse<WorkflowWebhookTrigger>> {
    return hubCall<ApiResponse<WorkflowWebhookTrigger>>(`/workflows/${workflowId}/webhooks`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  async updateWebhook(
    workflowId: string,
    triggerId: string,
    payload: UpdateWorkflowWebhookPayload,
  ): Promise<ApiResponse<WorkflowWebhookTrigger>> {
    return hubCall<ApiResponse<WorkflowWebhookTrigger>>(
      `/workflows/${workflowId}/webhooks/${triggerId}`,
      {
        method: "PATCH",
        body: JSON.stringify(payload),
      },
    );
  },

  async rotateWebhookSecret(
    workflowId: string,
    triggerId: string,
  ): Promise<ApiResponse<WorkflowWebhookTrigger>> {
    return hubCall<ApiResponse<WorkflowWebhookTrigger>>(
      `/workflows/${workflowId}/webhooks/${triggerId}/rotate-secret`,
      { method: "POST" },
    );
  },

  async deleteWebhook(
    workflowId: string,
    triggerId: string,
  ): Promise<ApiResponse<{ deleted: boolean }>> {
    return hubCall<ApiResponse<{ deleted: boolean }>>(
      `/workflows/${workflowId}/webhooks/${triggerId}`,
      { method: "DELETE" },
    );
  },

  /** POST /v1/cms/checkpoints/:id/cancel — requires workflow.edit ACL. */
  async cancelCheckpoint(
    id: string,
    payload: { reason?: string },
  ): Promise<ApiResponse<Checkpoint>> {
    return hubCall<ApiResponse<Checkpoint>>(`/checkpoints/${id}/cancel`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
};
